import { Link } from 'react-router-dom';
import { Twitter, Instagram, Facebook, Mail, Phone, MapPin, Building2, Heart, MessageCircle } from 'lucide-react';
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useTranslation } from 'react-i18next';
import { publicContact } from '@/config/publicContact';

const contact = publicContact;
const social = contact.social;

export function Footer() {
  const { t } = useTranslation('public', { keyPrefix: 'common.footer' });

  const footerLinks = {
    platform: [
      { label: t('platform.discover'), href: '/' },
      { label: t('platform.forBusiness'), href: '/for-business' },
      { label: t('platform.aboutUs'), href: '/about' },
      { label: t('platform.whatWeOffer'), href: '/what-we-offer' },
    ],
    support: [
      { label: t('support.contact'), href: '/contact' },
    ],
    business: [
      { label: t('business.login'), href: '/business' },
      { label: t('business.pricing'), href: '/for-business#pricing' },
      { label: t('business.features'), href: '/for-business#how-it-works' },
    ],
    contact: [
      ...(contact.generalEmail ? [{ label: contact.generalEmail, href: `mailto:${contact.generalEmail}`, icon: Mail, subLabel: undefined }] : []),
      ...contact.phones.map(p => ({
        label: p.number,
        href: p.href,
        icon: p.isWhatsApp ? MessageCircle : Phone,
        subLabel: p.label,
      })),
      ...(contact.address ? [{ label: contact.address, href: contact.addressHref || '#', icon: MapPin, subLabel: undefined }] : []),
    ],
    social: [
      ...(social.twitter ? [{ label: t('social.twitter'), href: social.twitter, icon: Twitter }] : []),
      ...(social.instagram ? [{ label: t('social.instagram'), href: social.instagram, icon: Instagram }] : []),
      ...(social.facebook ? [{ label: t('social.facebook'), href: social.facebook, icon: Facebook }] : []),
    ],
  };

  return (
    <footer className="bg-gradient-to-br from-emerald-600 to-emerald-700 border-t border-emerald-500/30 mt-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Main Footer Content */}
        <div className="py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            
            {/* Brand Section */}
            <div className="lg:col-span-3 space-y-6">
              <div className="space-y-4">
                <img src="/brand/wildy-lockup-white.svg" alt={t('brand.title')} className="h-10 w-auto" />
                <p className="text-emerald-100 leading-relaxed max-w-md">
                  {t('brand.description')}
                </p>
              </div>
            </div>

            {/* Navigation Links */}
            <div className="lg:col-span-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
                
                {/* Platform Links */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-white flex items-center gap-2">
                    <div className="w-6 h-6 rounded-lg bg-white/20 border border-white/30 flex items-center justify-center shadow-sm backdrop-blur-sm">
                      <Building2 className="w-3 h-3 text-white" />
                    </div>
                    {t('platform.title')}
                  </h4>
                  <ul className="space-y-3">
                    {footerLinks.platform.map((link) => (
                      <li key={link.href}>
                        <Link 
                          to={link.href}
                          className="text-emerald-100 hover:text-white transition-colors text-sm leading-relaxed hover:translate-x-1 transform transition-transform inline-block"
                        >
                          {link.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Support Links */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-white flex items-center gap-2">
                    <div className="w-6 h-6 rounded-lg bg-white/20 border border-white/30 flex items-center justify-center shadow-sm backdrop-blur-sm">
                      <Heart className="w-3 h-3 text-white" />
                    </div>
                    {t('support.title')}
                  </h4>
                  <ul className="space-y-3">
                    {footerLinks.support.map((link) => (
                      <li key={link.href}>
                        <Link 
                          to={link.href}
                          className="text-emerald-100 hover:text-white transition-colors text-sm leading-relaxed hover:translate-x-1 transform transition-transform inline-block"
                        >
                          {link.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Business Links */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-white flex items-center gap-2">
                    <div className="w-6 h-6 rounded-lg bg-white/20 border border-white/30 flex items-center justify-center shadow-sm backdrop-blur-sm">
                      <Building2 className="w-3 h-3 text-white" />
                    </div>
                    {t('business.title')}
                  </h4>
                  <ul className="space-y-3">
                    {footerLinks.business.map((link) => (
                      <li key={link.href}>
                        <Link 
                          to={link.href}
                          className="text-emerald-100 hover:text-white transition-colors text-sm leading-relaxed hover:translate-x-1 transform transition-transform inline-block"
                        >
                          {link.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Contact Information - Right Side */}
            <div className="lg:col-span-3 space-y-6">
              <Card className="border border-white/20 shadow-lg shadow-black/20 bg-white/10 backdrop-blur-sm">
                <CardContent className="p-4 space-y-3">
                  <h4 className="font-semibold text-white flex items-center gap-2">
                    <div className="w-6 h-6 rounded-lg bg-white/20 border border-white/30 flex items-center justify-center shadow-sm backdrop-blur-sm">
                      <Mail className="w-3 h-3 text-white" />
                    </div>
                    {t('contact.title')}
                  </h4>
                  {footerLinks.contact.map((item) => (
                    <a
                      key={item.href}
                      href={item.href}
                      className="flex items-center gap-3 text-emerald-100 hover:text-white transition-colors group"
                    >
                      <item.icon className="w-4 h-4 text-emerald-200 group-hover:text-white transition-colors shrink-0" />
                      <div className="flex flex-col min-w-0">
                        <span className="text-sm">{item.label}</span>
                        {item.subLabel && (
                          <span className="text-xs text-emerald-300 font-medium">{item.subLabel}</span>
                        )}
                      </div>
                    </a>
                  ))}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        <Separator className="bg-emerald-500/30" />

        {/* Bottom Footer */}
        <div className="py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            
            {/* Copyright */}
            <div className="text-center sm:text-left">
              <p className="text-sm text-emerald-100">
                {t('copyright', { year: new Date().getFullYear() })}
              </p>
              <p className="text-xs text-emerald-200 mt-1">
                {t('builtWith')}
              </p>
            </div>

            {/* Social Links: hidden until at least one VITE_SOCIAL_* URL is set */}
            {footerLinks.social.length > 0 && (
            <div className="flex items-center gap-4">
              <span className="text-sm text-emerald-100 hidden sm:block">{t('social.followUs')}:</span>
              <div className="flex items-center gap-3">
                {footerLinks.social.map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="min-w-[44px] min-h-[44px] w-11 h-11 rounded-lg bg-white/10 border border-white/20 shadow-sm hover:shadow-md hover:bg-white/20 transition-all duration-200 flex items-center justify-center group hover:scale-105 transform backdrop-blur-sm touch-manipulation"
                    aria-label={item.label}
                  >
                    <item.icon className="w-4 h-4 text-emerald-100 group-hover:text-white transition-colors" />
                  </a>
                ))}
              </div>
            </div>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
}